import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label, number } from "../../config";
import { openFavoriteFilterInTasksGrid } from "../../helper/openFavoriteFilterInTasksGrid";
import { GridContext } from "../../utils/kendo";
import "./bookmarks.scss";

const DRAG_MIME = "favourite-filter";

/**
 * Grid cell for favourite filters — same shell / classes as {@link MarkBookmark}
 * (projects row) so `bookmarks.scss` applies identically. Row click opens that
 * filter on Tasks (Grid); unfavourite star and drag handle do not navigate.
 * Parent: {@link BookmarksList} when `type === label.FAVOURITE_FILTERS_GRID_TYPE`.
 * @author Sejal
 */
const MarkFavouriteFilter = (props) => {
  const currentContext = React.useContext(GridContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state) => state.auth.user);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredBookmark, setHoveredBookmark] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const popupTimeoutRef = useRef(null);

  /** Personal-board favourites should display "{Name}'s Board" instead of the raw
   *  ProjectName (which the backend stores as "2"). Mirrors `formatGroupTitle` in
   *  FiltersPanel.jsx so the sidebar matches the Views grid grouping label. */
  const getDisplayProjectName = (dataItem) => {
    const isPersonal = dataItem?.ProjectId && user?.myProjectId && dataItem.ProjectId === user.myProjectId;
    if (isPersonal) {
      const displayName = (user?.firstName || user?.name || "My").toString();
      const cap = displayName.charAt(number.ZERO).toUpperCase() + displayName.slice(number.ONE);
      return `${cap}'s Board`;
    }
    return dataItem?.ProjectName || "";
  };

  const clearHoverTimeout = () => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearHoverTimeout();
  }, []);

  const truncateLine = (str, hovered) => {
    if (!str) return "";
    const limit = hovered ? number.SIXTEEN : number.TWENTY_EIGHT;
    return str.length > limit ? str.substring(number.ZERO, limit) + "..." : str;
  };

  const handleOnMouseEnter = (itemId) => {
    if (isDragging) return;
    clearHoverTimeout();
    setHoveredItem(itemId);
  };

  const handleOnMouseLeave = () => {
    clearHoverTimeout();
    popupTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
      setHoveredBookmark(null);
    }, number.SIX_HUNDRED);
  };

  const handleBookmarkOnMouseEnter = (itemId) => {
    if (isDragging) return;
    clearHoverTimeout();
    setHoveredItem(itemId);
    setHoveredBookmark(itemId);
  };

  const handleBookmarkOnMouseLeave = () => {
    clearHoverTimeout();
  };

  const handleBookmarkIconClick = async (e) => {
    e.stopPropagation();
    if (props.handleRemoveFavorite) {
      props.handleRemoveFavorite(props.dataItem);
    }
  };

  /** HTML5 drag does NOT fire a trailing `click` after a real drag completes,
   *  so we don't need any post-drag suppression timer here — same pattern as
   *  {@link MarkBookmark.handleProjectBookMarkClick}. We only guard the rare
   *  case where this handler is invoked while `isDragging` is still true (e.g.
   *  a stray click before `dragend` ran). */
  const handleRowOpenTasks = async (e) => {
    e.stopPropagation();
    if (isDragging) return;
    const dataItem = props.dataItem;
    const filterId = dataItem?.FilterId;
    const projectId = dataItem?.ProjectId;
    if (filterId == null || filterId === "" || projectId == null || projectId === "") return;
    const isPersonalBoard = Boolean(user?.myProjectId && String(projectId) === String(user.myProjectId));
    await openFavoriteFilterInTasksGrid({
      dispatch,
      history,
      user,
      filterId,
      projectId,
      projectName: dataItem?.ProjectName,
      isPersonalBoard,
    });
  };

  const onDragOverHandler = (e) => {
    if (!e) return;
    e.preventDefault();
    if (e.dataTransfer.types.includes("text/plain")) {
      e.dataTransfer.dropEffect = "move";
      currentContext.reorder(props.dataItem);
    }
  };

  const onDragStartHandler = (e) => {
    if (!e) return;
    setIsDragging(true);
    setHoveredItem(null);
    setHoveredBookmark(null);
    clearHoverTimeout();
    currentContext.dragStart(props.dataItem);
    e.dataTransfer.setData("text/plain", DRAG_MIME);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragEndHandler = () => {
    setIsDragging(false);
  };

  const onDropHandler = (e) => {
    if (!e) return;
    e.preventDefault();
    setIsDragging(false);
    const draggedData = e.dataTransfer.getData("text/plain");
    if (draggedData === DRAG_MIME) {
      currentContext.setActiveItem(null);
      currentContext.onDrop(e);
    }
  };

  const rowJsx = () => {
    const id = props.dataItem?.FilterId;
    const isHovered = hoveredItem === id && !isDragging;
    const hasOpenPopup = isHovered;

    return (
      <div
        className={`position-relative p-2 bookmark-hover-container ${hasOpenPopup ? "bookmark-item-with-popup ps-3" : ""}`}
        id={`favourite-filter-${id}`}
        onMouseEnter={() => handleOnMouseEnter(id)}
        onMouseLeave={handleOnMouseLeave}>
        <div className='text-left'>
          {isHovered && (
            <span className='drag-icon cursor-move' onMouseDown={(ev) => ev.stopPropagation()} onClick={(ev) => ev.stopPropagation()}>
              {icon.DRAG_INDICATOR}
            </span>
          )}
          <span className='bookmark-text cursor-pointer'>{truncateLine(props.dataItem?.FilterName || "", Boolean(hoveredItem === id))}</span>
          <div className='mt-1'>
            <span className='bookmark-name-text'>{truncateLine(getDisplayProjectName(props.dataItem), Boolean(hoveredItem === id))}</span>
          </div>
          {isHovered && (
            <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
              <span
                className='bookmark-icon-popup position-absolute'
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  handleBookmarkOnMouseEnter(id);
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation();
                  handleBookmarkOnMouseLeave();
                }}
                onClick={handleBookmarkIconClick}>
                {hoveredBookmark === id ? (
                  <span>{icon.BOOKMARK_FAVORITE}</span>
                ) : (
                  <span title={label.REMOVE_FAVORITE}>{icon.BOOKMARK_MARK_FAVORITE}</span>
                )}
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <td>
        <div className='text-truncate' onDragOver={onDragOverHandler} onDrop={onDropHandler}>
          <div
            id={`favourite-filter-wrap-${props.dataItem?.FilterId}`}
            className='cursor-pointer d-block'
            draggable
            onDragStart={onDragStartHandler}
            onDragEnd={onDragEndHandler}
            onClick={handleRowOpenTasks}>
            {rowJsx()}
          </div>
        </div>
      </td>
    </>
  );
};

export default MarkFavouriteFilter;
